/**
 * PT-02 Punctuation entropy (INV-CH-PT-02, ENGINE-SPEC §3.5, §4; change C-PT02-CS).
 *
 * Marks are the seed's set [.,;:!?—–()"'“”‘’] found in PROSE-register text. Within each joint
 * segment the Chao–Shen coverage-adjusted entropy (nats) of the mark distribution is computed, and
 * the document value is the mark-count-weighted average of the segment entropies (segments with
 * fewer than 20 marks are pooled into one remainder bucket; a remainder under 20 marks is
 * dropped). Hn = H / ln(10), the seed's H/log2(10) with H in bits. The seed used the plug-in
 * Shannon entropy of the whole document, which is biased low in short texts and blind to section
 * changes. The between-segment spread of Hn is reported as a feature (rhythm-drift diagnostic).
 * The interval uses the delta-method variance of the plug-in entropy, pooled over the buckets.
 */
import { chaoShenEntropy } from '../util/stats.ts';
import { abstainResult, derivePrior, languageGate, scoredResult } from './framework.ts';
import { SMALL, jointRanges, proseParagraphs, segmentOfParagraph } from './helpers.ts';
import type { ChannelDef, ChannelImpl, ChannelResult, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0.15, 70],
	[0.3, 58],
	[0.45, 44],
	[0.6, 30],
	[0.8, 18],
];
const MARKS = '.,;:!?—–()"\'“”‘’';
const NM = MARKS.length;
const LUT = (() => {
	const t = new Int8Array(0x2020).fill(-1);
	for (let i = 0; i < NM; i++) t[MARKS.charCodeAt(i)] = i;
	return t;
})();
const MIN_BUCKET = 20;
const LN10 = Math.log(10);

export const pt02Def: ChannelDef = {
	id: 'PT-02',
	name: 'Punctuation entropy',
	family: 'RHYTHM',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'distributional',
	features: [
		{ id: 'hn', label: 'Normalised Chao–Shen punctuation entropy (within joint segments)' },
	],
	minExposure: 20,
	exposureUnit: 'marks',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'normalised punctuation entropy',
	prior: derivePrior(RAMP, 1, 'marks', 1.2),
	weight: 0.5,
	nd: 1,
	changes: ['C-PT02-CS'],
};

/** Plug-in entropy variance terms: Σp ln²p − (Σp ln p)² (delta method, per observation). */
function entropyVarianceTerm(counts: ArrayLike<number>, n: number): number {
	let a = 0;
	let b = 0;
	for (let i = 0; i < counts.length; i++) {
		const c = counts[i] as number;
		if (c <= 0) continue;
		const p = c / n;
		const l = Math.log(p);
		a += p * l * l;
		b += p * l;
	}
	return Math.max(0, a - b * b);
}

/** Number of punctuation marks counted (the length of a per-segment count vector). */
export const PT02_MARKS = NM;

/** Adds the counts of the seed's punctuation marks in `text[from, to)` to `out[base + mark]`. */
export function countMarks(
	text: string,
	from: number,
	to: number,
	out: Float64Array,
	base: number
): void {
	for (let i = from; i < to; i++) {
		const c = text.charCodeAt(i);
		if (c >= LUT.length) continue;
		const m = LUT[c] as number;
		if (m >= 0) out[base + m] = (out[base + m] as number) + 1;
	}
}

export interface Pt02Value {
	total: number;
	value: number;
	se: number;
	wsum: number;
	hnWhole: number;
	between: number;
	buckets: number;
	H: number;
}

/**
 * The document value from per-joint-segment mark counts (`counts[s * NM + m]`): Chao–Shen entropies of the segments with
 * enough marks and a pooled remainder, weighted by mark count. Null below the exposure minimum.
 */
export function pt02Value(counts: Float64Array, nSeg: number): Pt02Value | null {
	const segTotal = new Float64Array(nSeg);
	let total = 0;
	for (let s = 0; s < nSeg; s++) {
		let t = 0;
		for (let m = 0; m < NM; m++) t += counts[s * NM + m] as number;
		segTotal[s] = t;
		total += t;
	}
	if (total < pt02Def.minExposure) return null;

	const wholeCounts = new Float64Array(NM);
	for (let s = 0; s < nSeg; s++)
		for (let m = 0; m < NM; m++)
			wholeCounts[m] = (wholeCounts[m] as number) + (counts[s * NM + m] as number);

	// buckets: segments with ≥ 20 marks, plus one pooled remainder (dropped when it has < 20 marks)
	const buckets: Float64Array[] = [];
	const remainder = new Float64Array(NM);
	let remTotal = 0;
	for (let s = 0; s < nSeg; s++) {
		const slice = counts.subarray(s * NM, (s + 1) * NM);
		if ((segTotal[s] as number) >= MIN_BUCKET) buckets.push(slice);
		else {
			for (let m = 0; m < NM; m++) remainder[m] = (remainder[m] as number) + (slice[m] as number);
			remTotal += segTotal[s] as number;
		}
	}
	if (remTotal >= MIN_BUCKET) buckets.push(remainder);
	// every segment tiny: the whole document is one bucket (its total is ≥ 20 here)
	if (buckets.length === 0) buckets.push(wholeCounts);

	let wsum = 0;
	let hsum = 0;
	let varSum = 0;
	const hn: number[] = [];
	const wts: number[] = [];
	for (const b of buckets) {
		let n = 0;
		for (let m = 0; m < NM; m++) n += b[m] as number;
		if (n <= 0) continue;
		const h = chaoShenEntropy(b);
		hsum += n * h;
		wsum += n;
		varSum += n * n * (entropyVarianceTerm(b, n) / n);
		hn.push(h / LN10);
		wts.push(n);
	}
	const H = hsum / wsum;
	const value = H / LN10;
	const se = Math.sqrt(varSum) / wsum / LN10;
	const mHn = hn.reduce((a, x, i) => a + x * (wts[i] as number), 0) / wsum;
	const between = Math.sqrt(
		hn.reduce((a, x, i) => a + (wts[i] as number) * (x - mHn) ** 2, 0) / wsum
	);
	const hnWhole = chaoShenEntropy(wholeCounts) / LN10;
	return { total, value, se, wsum, hnWhole, between, buckets: hn.length, H };
}

function run(ctx: RunContext): ChannelResult {
	const def = pt02Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const ranges = jointRanges(ctx);
	const P = doc.paragraphs.count;
	const segOf = segmentOfParagraph(ranges, P);
	const nSeg = ranges.length;

	// mark counts per joint segment (prose paragraphs' own blocks only)
	const counts = new Float64Array(nSeg * NM);
	const text = doc.text;
	for (const p of proseParagraphs(doc)) {
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		countMarks(text, b.charStart, b.charEnd, counts, (segOf[p] as number) * NM);
	}
	const v = pt02Value(counts, nSeg);
	if (!v)
		return abstainResult(
			def,
			`insufficient text: ${counts.reduce((a, x) => a + x, 0)} marks < ${def.minExposure}`,
			notes
		);
	const { total, value, se, wsum, hnWhole, between, H } = v;
	const ci90: [number, number] = [Math.max(0, value - 1.645 * se), value + 1.645 * se];
	const kappa = wsum / (wsum + def.prior.m);

	return scoredResult(def, {
		K: total,
		E: total,
		raw: value,
		estimate: value,
		ci90,
		deviation: (def.direction * (value - def.prior.mu)) / Math.max(se, SMALL),
		phi: 1,
		kappa,
		notes,
		features: {
			hn: value,
			hnWhole,
			hnBetweenSegmentSd: between,
			buckets: v.buckets,
			marks: total,
		},
		summary: [`H = ${(H / Math.LN2).toFixed(2)} bits (normalised ${value.toFixed(2)})`],
	});
}

export const pt02: ChannelImpl = { def: pt02Def, phase: 'derived', run };
